import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import merge from 'deepmerge';
import warning from 'tiny-warning';
import isEqual from 'lodash/isEqual';
import debounce from 'debounce-fn';
import { initialize } from 'launchdarkly-js-client-sdk';
import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var adapterState = {
  isReady: false,
  isConfigured: false,
  user: undefined,
  client: undefined,
  flags: {}
};

var updateFlagsInAdapterState = function updateFlagsInAdapterState(updatedFlags) {
  adapterState.flags = _objectSpread({}, adapterState.flags, {}, updatedFlags);
};

var getFlag = function getFlag(flagName) {
  return adapterState.flags[flagName];
};

var didFlagChange = function didFlagChange(flagName, nextFlagValue) {
  var previousFlagValue = getFlag(flagName);
  if (previousFlagValue === undefined) return true;
  return previousFlagValue !== nextFlagValue;
};

var getIsReady = function getIsReady() {
  return adapterState.isReady;
};

var normalizeFlag = function normalizeFlag(flagName, flagValue) {
  return [camelCase(flagName), // Multi variate flags contain a string or `null` - `false` seems more natural.
  flagValue === null || flagValue === undefined ? false : flagValue];
};

var denormalizeFlagName = function denormalizeFlagName(flagName) {
  return kebabCase(flagName);
};

var setupFlagSubcription = function setupFlagSubcription(_ref) {
  var flagsFromSdk = _ref.flagsFromSdk,
      onFlagsStateChange = _ref.onFlagsStateChange,
      flagsUpdateDelayMs = _ref.flagsUpdateDelayMs;

  var _loop = function _loop(flagName) {
    // Dispatch whenever a configured flag value changes
    if (Object.prototype.hasOwnProperty.call(flagsFromSdk, flagName) && adapterState.client) {
      adapterState.client.on("change:".concat(flagName), function (flagValue) {
        var _normalizeFlag = normalizeFlag(flagName, flagValue),
            _normalizeFlag2 = _slicedToArray(_normalizeFlag, 2),
            normalizedFlagName = _normalizeFlag2[0],
            normalizedFlagValue = _normalizeFlag2[1]; // Sometimes the SDK flushes flag changes without a value having changed.


        if (!didFlagChange(normalizedFlagName, normalizedFlagValue)) return;

        var updatedFlags = _defineProperty({}, normalizedFlagName, normalizedFlagValue); // NOTE: Adapter state needs to be updated outside of debounced-fn
        // so that no flag updates are lost.


        updateFlagsInAdapterState(updatedFlags);

        var updateFlags = function updateFlags() {
          onFlagsStateChange(adapterState.flags);
        };

        debounce(updateFlags, {
          wait: flagsUpdateDelayMs,
          immediate: !flagsUpdateDelayMs
        })();
      });
    }
  };

  for (var flagName in flagsFromSdk) {
    _loop(flagName);
  }
};

var getIsAnonymousUser = function getIsAnonymousUser(user) {
  return !(user === null || user === void 0 ? void 0 : user.key);
};

var ensureUser = function ensureUser(user) {
  var isAnonymousUser = getIsAnonymousUser(user); // NOTE: When marked `anonymous` the SDK will generate a unique key and cache it in local storage

  return merge(user, {
    key: isAnonymousUser ? undefined : user.key,
    anonymous: isAnonymousUser
  });
};

var initializeClient = function initializeClient(clientSideId, user, clientOptions) {
  return initialize(clientSideId, user, clientOptions);
};

var changeUserContext = function changeUserContext(nextUser) {
  return adapterState.client && adapterState.client.identify ? adapterState.client.identify(nextUser) : Promise.reject(new Error('Can not change user context: client not yet initialized.'));
};

var updateUserContext = function updateUserContext(updatedUserProps) {
  var isAdapterReady = adapterState.isConfigured && adapterState.isReady;
  warning(isAdapterReady, '@flopflip/launchdarkly-adapter: adapter not ready and configured. User context can not be updated before.');
  if (!isAdapterReady) return Promise.reject(new Error('Can not update user context: adapter not yet ready.'));
  return changeUserContext(_objectSpread({}, adapterState.user, {}, updatedUserProps));
}; // NOTE: Exported for testing only


var normalizeFlags = function normalizeFlags(rawFlags) {
  return Object.entries(rawFlags).reduce(function (normalizedFlags, _ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        flagName = _ref3[0],
        flagValue = _ref3[1];

    var _normalizeFlag3 = normalizeFlag(flagName, flagValue),
        _normalizeFlag4 = _slicedToArray(_normalizeFlag3, 2),
        normalizedFlagName = _normalizeFlag4[0],
        normalizedFlagValue = _normalizeFlag4[1]; // Can't return expression as it is the assigned value


    normalizedFlags[normalizedFlagName] = normalizedFlagValue;
    return normalizedFlags;
  }, {});
};

var getInitialFlags = function getInitialFlags(_ref4) {
  var onFlagsStateChange = _ref4.onFlagsStateChange,
      onStatusStateChange = _ref4.onStatusStateChange,
      flags = _ref4.flags,
      throwOnInitializationFailure = _ref4.throwOnInitializationFailure;

  if (adapterState.client) {
    return adapterState.client.waitForInitialization().then(function () {
      var flagsFromSdk = null;

      if (adapterState.client && !flags) {
        flagsFromSdk = adapterState.client.allFlags();
      } else if (adapterState.client && flags) {
        flagsFromSdk = {};

        for (var _i = 0, _Object$entries = Object.entries(flags); _i < _Object$entries.length; _i++) {
          var _ref7 = _Object$entries[_i];

          var _ref6 = _slicedToArray(_ref7, 2);

          var requestedFlagName = _ref6[0];
          var defaultFlagValue = _ref6[1];
          var denormalizedRequestedFlagName = denormalizeFlagName(requestedFlagName);
          flagsFromSdk[denormalizedRequestedFlagName] = adapterState.client.variation(denormalizedRequestedFlagName, defaultFlagValue);
        }
      }

      if (flagsFromSdk) {
        var _flags = normalizeFlags(flagsFromSdk);

        updateFlagsInAdapterState(_flags); // ...and flush initial state of flags

        onFlagsStateChange(_flags);
      } // First update internal state


      adapterState.isReady = true; // ...to then signal that the adapter is ready

      onStatusStateChange({
        isReady: true
      });
      return Promise.resolve({
        flagsFromSdk: flagsFromSdk
      });
    }).catch(function () {
      if (throwOnInitializationFailure) return Promise.reject(new Error('@flopflip/launchdarkly-adapter: adapter failed to initialize.'));
      return Promise.resolve({
        flagsFromSdk: null
      });
    });
  }

  return Promise.reject(new Error('@flopflip/launchdarkly-adapter: can not subscribe with non initialized client.'));
};

var configure = function configure(_ref8) {
  var clientSideId = _ref8.clientSideId,
      user = _ref8.user,
      _ref8$clientOptions = _ref8.clientOptions,
      clientOptions = _ref8$clientOptions === void 0 ? {} : _ref8$clientOptions,
      onFlagsStateChange = _ref8.onFlagsStateChange,
      onStatusStateChange = _ref8.onStatusStateChange,
      flags = _ref8.flags,
      _ref8$subscribeToFlag = _ref8.subscribeToFlagChanges,
      subscribeToFlagChanges = _ref8$subscribeToFlag === void 0 ? true : _ref8$subscribeToFlag,
      _ref8$throwOnInitiali = _ref8.throwOnInitializationFailure,
      throwOnInitializationFailure = _ref8$throwOnInitiali === void 0 ? false : _ref8$throwOnInitiali,
      flagsUpdateDelayMs = _ref8.flagsUpdateDelayMs;
  adapterState.user = ensureUser(user);
  adapterState.client = initializeClient(clientSideId, adapterState.user, clientOptions);
  adapterState.isConfigured = true;
  return getInitialFlags({
    onFlagsStateChange: onFlagsStateChange,
    onStatusStateChange: onStatusStateChange,
    flags: flags,
    throwOnInitializationFailure: throwOnInitializationFailure
  }).then(function (_ref9) {
    var flagsFromSdk = _ref9.flagsFromSdk;
    if (subscribeToFlagChanges && flagsFromSdk) setupFlagSubcription({
      flagsFromSdk: flagsFromSdk,
      flagsUpdateDelayMs: flagsUpdateDelayMs,
      onFlagsStateChange: onFlagsStateChange
    });
    return adapterState.client;
  });
};

var reconfigure = function reconfigure(_ref10) {
  var nextUser = _ref10.user;
  if (!adapterState.isConfigured) return Promise.reject(new Error('@flopflip/launchdarkly-adapter: please configure adapter before reconfiguring.'));

  if (!isEqual(adapterState.user, nextUser)) {
    adapterState.user = ensureUser(nextUser);
    return changeUserContext(adapterState.user);
  }

  return Promise.resolve();
};

var adapter = {
  configure: configure,
  reconfigure: reconfigure,
  getFlag: getFlag,
  getIsReady: getIsReady,
  updateUserContext: updateUserContext
};

var version = '2.9.4';

export default adapter;
export { version };
//# sourceMappingURL=@flopflip-launchdarkly-adapter.es.js.map
